import { core } from '@musinsa/pbo-core'
import { InventorySupplementOutcome, IvlInventoryResult } from '@/services/inventory/inventoryStatus/ivlTypes'
import { IVL_INVENTORY_DISPLAY_COUNT_CAP } from '@/services/inventory/inventoryStatus/ivlConstants'
import { ivlInventoryErrorMessage } from '@/services/inventory/inventoryStatus/ivlErrorMessages'

/** 정확한 건수도 1000 이상이면 표시만 1000+로 축약한다. 원본은 페이저·다운로드 검증에 보존한다. */
export const ivlCountDisplay = (total: IvlInventoryResult['pageInfo']['totalCount']): string => {
  if (total == null) return '미집계'
  return total >= IVL_INVENTORY_DISPLAY_COUNT_CAP ? `${IVL_INVENTORY_DISPLAY_COUNT_CAP}+` : total.toLocaleString()
}

// Spec: docs/spec/inventory/SPEC-inventory-ivl-sku-id-A.yaml#ivl-table-hides-goods-code-and-option-code
const REMOVED_COLUMNS = new Set(['GDS_ATTR1', 'WH_AREA', 'LOT_ATTR5', 'PRIORITY', 'GDS_CD', 'option_code'])
const ADDED_COLUMNS_AFTER: Record<string, core.CoreColumnType> = {
  // Spec: docs/spec/inventory/SPEC-inventory-ivl-sku-id-A.yaml#ivl-table-shows-sku-id-column
  STL_NO: { dataIndex: 'SKU_ID', title: 'SKU_ID', width: 160 },
  ALLOCQTY: { dataIndex: 'REMAINQTY', title: '잔여수량', width: 120, align: 'right' },
}
const amountFormatter = new Intl.NumberFormat('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const PRICE_COLUMNS = [
  { dataIndex: 'PAYABLE_PRICE', title: '매입단가' },
  { dataIndex: 'RECEIVABLE_PRICE', title: '매출단가' },
  { dataIndex: 'SALES_AMOUNT', title: '매출총액' },
] as const

export const toIvlColumns = (columns: core.CoreColumnType[]): core.CoreColumnType[] => [
  ...columns.filter((column) => typeof column.dataIndex !== 'string' || !REMOVED_COLUMNS.has(column.dataIndex)).flatMap((column): core.CoreColumnType[] => {
    const added = typeof column.dataIndex === 'string' ? ADDED_COLUMNS_AFTER[column.dataIndex] : undefined
    return added ? [column, added] : [column]
  }),
  ...PRICE_COLUMNS.map((column): core.CoreColumnType => ({
    ...column,
    width: 140,
    align: 'right',
    render: (value: unknown) => (typeof value === 'number' ? amountFormatter.format(value) : value ?? ''),
  })),
]

const failedItems = (outcome: InventorySupplementOutcome): Set<number> =>
  new Set(outcome.status === 'PARTIAL' ? outcome.failures.map(({ itemIndex }) => itemIndex) : [])

/** itemIndex는 서버 배열 기준이다. 행을 필터링하거나 정렬하기 전에 실패 셀에 연결한다. */
export const toIvlViewRows = (result: IvlInventoryResult): Record<string, unknown>[] => {
  const attributes = result.supplement.productAttributes
  const prices = result.supplement.prices
  const attributeFailures = failedItems(attributes)
  const priceFailures = failedItems(prices)
  return result.items.map((item, index) => ({
    key: `${result.pageInfo.page}:${index}`,
    service_type: item.serviceType,
    shipper_name: item.shipperName,
    SPR_NM: item.sprNm,
    GDS_GROUP1: attributes.status === 'FAILED' || attributeFailures.has(index) ? '확인 불가' : item.gdsGroup1,
    BRD_NM: item.brdNm,
    STL_NO: item.stlNo,
    SKU_ID: item.skuId,
    BARCODE: item.barcode,
    GDS_NM: item.gdsNm,
    GDS_OPT: item.gdsOpt,
    GDS_COLOR: attributes.status === 'FAILED' || attributeFailures.has(index) ? '확인 불가' : item.gdsColor,
    TOTALQTY: item.totalQty,
    AVAILQTY: item.availQty,
    ALLOCQTY: item.allocQty,
    REMAINQTY: item.remainQty,
    WH_NM: item.whNm,
    PAYABLE_PRICE: prices.status === 'FAILED' || priceFailures.has(index) ? '확인 불가' : item.payablePrice,
    RECEIVABLE_PRICE: prices.status === 'FAILED' || priceFailures.has(index) ? '확인 불가' : item.receivablePrice,
    SALES_AMOUNT: prices.status === 'FAILED' || priceFailures.has(index) ? '확인 불가' : item.salesAmount,
  }))
}

export const inventoryErrorMessage = ivlInventoryErrorMessage

export const inventoryEmptyMessage = (result?: IvlInventoryResult, hasSearched?: boolean, error?: unknown): string => {
  if (error) return '재고 조회에 실패했어요.'
  if (!hasSearched) return '조건을 입력한 뒤 조회해 주세요.'
  if (!result) return '재고를 조회하고 있어요.'
  if (result.failures.length > 0) return '재고를 확인하지 못했어요.'
  if ((result.pageInfo.totalCount != null && result.pageInfo.totalCount > 0) || result.pageInfo.hasMore === true || result.pageInfo.page > 0) return '현재 페이지에 표시할 결과가 없어요.'
  if (result.summary.complete === false) return '재고를 확인하지 못했어요.'
  return '조회 결과가 없어요.'
}

/** 요청 오류, 부가정보 상태와 현재 페이지의 실패 상세를 표시한다. */
export function IvlInventoryFeedback({ result, error, warehouseError }: {
  result?: IvlInventoryResult
  error?: unknown
  warehouseError?: unknown
}) {
  if (error) return <div role="alert">{inventoryErrorMessage(error)}</div>
  return (
    <core.Flex vertical gap={8}>
      {warehouseError ? <div role="alert">센터 목록을 불러오지 못했어요. 화면을 새로고침한 뒤 다시 선택해 주세요.</div> : null}
      {result && (['productAttributes', 'prices'] as const).map((key) => {
        const outcome = result.supplement[key]
        if (outcome.status !== 'FAILED' && outcome.status !== 'PARTIAL') return null
        const name = key === 'prices' ? '단가' : '상품정보'
        return <div role="alert" key={key}>{name}{outcome.status === 'PARTIAL' ? ' 일부' : ''}를 확인하지 못했어요. 해당 셀은 확인 불가로 표시해요.</div>
      })}
      {result && result.failures.length > 0 ? (
        <details>
          <summary>현재 페이지의 재고 확인 실패 {result.failures.length.toLocaleString()}건</summary>
          <ul>
            {result.failures.map((failure, index) => (
              <li key={`${failure.errorRef}:${index}`}>
                {failure.reason}
                <div>문의 참조번호: {failure.errorRef}</div>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </core.Flex>
  )
}
